import { Credentials } from 'realm-web';
import { Loading } from 'quasar';
import { ref, computed, reactive } from 'vue';
import useVuelidate from '@vuelidate/core';
import {
  required,
  email as emailFormat,
  sameAs,
  minLength,
  helpers
} from '@vuelidate/validators';
import { useUser } from 'src/store/user';
import { usePatient } from 'src/store/patient';
import { useRelated } from 'src/store/relatedPatient';
import { useAppointments } from 'src/store/appointments';
import { Patient } from 'fhir/r4';
import { GenderType } from 'src/store/patient/state';

const oneLowerCase = helpers.regex(/^(?=.*[a-z]).*$/) as ((
  value: string
) => boolean);
const oneUpperCase = helpers.regex(/^(?=.*[A-Z]).*$/) as ((
  value: string
) => boolean);
const oneNumber = helpers.regex(/^(?=.*\d).*$/) as ((value: string) => boolean);

async function readData() {
  const patientStore = usePatient();
  const relatedStore = useRelated();
  const appStore = useAppointments();
  await Promise.all([
    patientStore.read().catch(console.error),
    relatedStore.get().catch(console.error),
    appStore.get().catch(console.error),
    appStore.getHistory().catch(console.error)
  ]);
}

const name = ref('');
const lastName = ref('');
const birthDate = ref('');
const gender = ref<GenderType>('unknown');
const prevition = ref('private');
const identifier = ref('');
const phone = ref('');
const emailField = ref('');
const preLoadedData = reactive({
  name,
  lastName,
  identifier,
  birthDate,
  gender,
  prevition,
  phone,
  email: emailField
});

export function registerComposable() {
  const userStore = useUser();
  const patientStore = usePatient();
  const loading = ref(false);

  function register(
    email: string,
    password: string,
    payload: Patient,
    code: string
  ) {
    loading.value = true;
    return patientStore
      .create(payload, code)
      .then(() => {
        return userStore
          .register({ email: email.toLowerCase(), password })
          .then(() => {
            return logInLink(email.toLowerCase(), password);
          });
      })
      .finally(() => {
        loading.value = false;
      });
  }

  function logInLink(email: string, password: string) {
    loading.value = true;
    const credentials = Credentials.emailPassword(email, password);
    return userStore
      .logInLink(credentials)
      .then(readData)
      .catch(console.error)
      .finally(() => {
        loading.value = false;
      });
  }

  return {
    register,
    loading,
    preLoadedData
  };
}

export default function user() {
  const userStore = useUser();
  const patientStore = usePatient();
  const relatedStore = useRelated();
  const appStore = useAppointments();
  const emailField = ref('');
  const passwordField = ref('');
  const rePasswordField = ref('');
  const termsField = ref(false);
  const loading = ref(false);

  const emailRules = computed(() => ({
    emailField: { required, email: emailFormat }
  }));

  const passwordRules = computed(() => ({
    passwordField: {
      required,
      minLength: minLength(8),
      oneLowerCase,
      oneUpperCase,
      oneNumber
    },
    rePasswordField: { required, sameAsPassword: sameAs(passwordField) }
  }));

  const credentialsRules = computed(() => ({
    emailField: emailRules.value.emailField,
    passwordField: passwordRules.value.passwordField,
    rePasswordField: passwordRules.value.rePasswordField,
    termsField: { sameAs: sameAs(true) }
  }));

  const credentialsFields = reactive({
    emailField,
    passwordField,
    rePasswordField,
    termsField
  });

  const emailFields = reactive({
    emailField
  });

  const passwordFields = reactive({
    passwordField,
    rePasswordField
  });

  const emailValidator = useVuelidate(emailRules, emailFields);
  const credentialsValidator = useVuelidate(
    credentialsRules,
    credentialsFields,
    { $scope: 'credentials', $registerAs: 'credentials' }
  );
  const passwordValidator = useVuelidate(passwordRules, passwordFields);

  async function initUser() {
    if (!userStore.identifier) {
      await logInAnon();
    } else if (userStore.type !== 'anon-user') {
      await readData();
    }
    userStore.loading = false;
  }

  function logIn() {
    loading.value = true;
    const credentials = Credentials.emailPassword(
      emailField.value.toLowerCase(),
      passwordField.value
    );
    return userStore
      .logIn(credentials)
      .then(async () => {
        await readData();
      })
      .finally(() => {
        loading.value = false;
      });
  }

  function logInAnon() {
    const credentials = Credentials.anonymous();
    return userStore.logIn(credentials);
  }

  function logInWithJWT(jwt: string) {
    Loading.show();
    const credentials = Credentials.jwt(jwt);
    return userStore
      .logIn(credentials)
      .then(async () => {
        await readData();
      })
      .finally(() => {
        Loading.hide();
      });
  }

  function callResetPassword() {
    loading.value = true;
    return userStore
      .callResetPassword(emailField.value.toLowerCase())
      .finally(() => {
        loading.value = false;
      });
  }

  function resetPassword(token: string, tokenId: string, password: string) {
    loading.value = true;
    return userStore.resetPassword(token, tokenId, password).finally(() => {
      loading.value = false;
    });
  }

  function logOut() {
    Loading.show();
    return userStore.logOut().finally(() => {
      patientStore.$reset();
      appStore.$reset();
      relatedStore.$reset();
      initUser()
        .then(() => {
          Loading.hide();
        })
        .catch(console.error);
    });
  }

  return {
    initUser,
    emailField,
    passwordField,
    rePasswordField,
    emailValidator,
    passwordValidator,
    credentialsValidator,
    credentialsFields,
    loading,
    logIn,
    logInAnon,
    callResetPassword,
    resetPassword,
    logOut,
    logInWithJWT
  };
}
